import classNames from 'classnames';
import Image, { StaticImageData } from 'next/image';
import { StartNowButton } from './components/StartNowButton';
import { useEffect, useState } from 'react';
import imgCollections from 'assets/images/product-showcase-collections.png';
import imgMemos from 'assets/images/product-showcase-memos.png';
import imgBoxes from 'assets/images/product-showcase-boxes.png';
import imgSharing from 'assets/images/product-showcase-sharing.png';

const items: {
  index: number;
  headerClassName: string;
  circleClassName: string;
  label: string;
  img: StaticImageData;
  alt: string;
}[] = [
  {
    index: 0,
    headerClassName: 'text-mf-blue!',
    circleClassName: 'bg-mf-blue-50!',
    label: 'Boost organization',
    img: imgCollections,
    alt: 'Illustration representing organisation and productivity',
  },
  {
    index: 1,
    headerClassName: 'text-mf-lilac!',
    circleClassName: 'bg-mf-lilac-50!',
    label: 'Capture essentials',
    img: imgMemos,
    alt: 'Graphic showing note-taking or capturing important ideas',
  },
  {
    index: 2,
    headerClassName: 'text-mf-green!',
    circleClassName: 'bg-mf-green-50!',
    label: 'Simplify everything',
    img: imgBoxes,
    alt: 'Visual metaphor for simplifying complex information',
  },
  {
    index: 3,
    headerClassName: 'text-mf-yellow!',
    circleClassName: 'bg-mf-yellow-50!',
    label: 'Share easily',
    img: imgSharing,
    alt: 'Illustration of easy sharing or collaboration tools',
  },
];

export const Section2 = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeItem = items[activeIndex];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % items.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.top.container}>
        <div className="relative mx-auto w-full">
          {/* Animated text */}
          {items.map((item, index) => (
            <div
              key={item.index}
              className={styles.top.animatedText(
                activeIndex === index,
                item.headerClassName,
              )}
            >
              {item.label}
            </div>
          ))}

          {/* Static text */}
          <span className={styles.top.staticText}>with MindFeed</span>
        </div>

        {/* Circle */}
        <div
          className={classNames(styles.top.circle, activeItem.circleClassName)}
        />

        {/* Images */}
        <div className={styles.top.images.container}>
          <div className={styles.top.images.inner}>
            {items.map((item, index) => (
              <div
                key={item.index}
                className={styles.top.images.wrapper(activeIndex === index)}
              >
                <Image
                  src={item.img}
                  alt={item.alt}
                  fill
                  className={styles.top.images.img}
                  sizes="262px"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.bottom.container}>
        <p className={styles.bottom.description}>
          Bring everything together in one place with MindFeed and keep your
          ideas organized effortlessly.
        </p>
        <div className={styles.bottom.buttonWrapper}>
          <StartNowButton />
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: 'max-w-7xl pt-20 lg:pt-30 pb-9 px-5 mx-auto lg:pb-36',
  top: {
    container: classNames('relative', 'w-full h-[353px] lg:h-[449px]'),
    animatedText: (isActive: boolean, className?: string) => {
      return classNames(
        className || '',
        'absolute z-10 left-1/2 transform -translate-x-1/2 w-full', // center horizontally
        'font-bold text-center text-5xl',
        'transition-all duration-500 ease-in-out',
        'block lg:text-[70px] lg:text-right lg:pr-110',
        isActive
          ? 'opacity-100 -top-[100px] lg:top-[180px] translate-y-8 lg:translate-y-0 lg:h-auto delay-500'
          : 'opacity-0 -top-[50px] lg:top-[200px] translate-y-8 lg:-translate-y-20 pointer-events-none delay-0',
      );
    },
    staticText: classNames(
      'absolute z-10 left-1/2 transform -translate-x-1/2 w-full', // center horizontally
      'font-bold text-center text-5xl text-mf-off-black-600',
      'top-4 lg:w-auto lg:top-[320px] lg:text-[70px] lg:left-auto lg:right-90 lg:-translate-x-0',
    ),
    circle: classNames(
      'mx-auto rounded-full w-[353px] h-[353px]',
      'lg:w-[400px] lg:h-[400px] lg:ml-auto lg:mr-20',
      'transition-colors duration-700 ease-in-out',
    ),
    images: {
      container: classNames('absolute w-full top-0 z-10'),
      inner: 'relative',
      wrapper: (isActive: boolean) =>
        classNames(
          'absolute w-[242px] h-[300px] z-10 transition-all duration-700 ease-in-out',
          'left-1/2 transform -translate-x-1/2',
          'lg:w-[262px] lg:h-[325px] lg:left-auto lg:right-40 lg:-translate-x-0',
          isActive
            ? 'opacity-100 top-[40px] translate-y-0'
            : 'opacity-0 top-[80px] -translate-y-4 pointer-events-none',
        ),
      img: 'object-contain',
    },
  },
  bottom: {
    container: 'flex flex-col items-center justify-center',
    description:
      'text-center mt-10 text-xl text-mf-off-black-500 leading-8 z-10 lg:text-left',
    buttonWrapper: 'mt-12',
  },
};
